import {applicationContext} from "@@/package/application/applicationContext";
import {useLearningStore} from "~/store/learning/learningStore";
import {useUserStore} from "~/store/user/userStore";
import { S3Client, ListObjectsCommand, PutObjectCommand, DeleteObjectsCommand } from "@aws-sdk/client-s3";
import {type FileSystemTree} from "@webcontainer/api";
import {useWebContainerService} from "~/composables/service/useWebContainerService";
import DialogType from "~/components/Common/Dialog/model/DialogType";
import LearningConfirmPopupType from "~/components/Common/Dialog/dialog/LearningConfirmPopup/LearningConfirmPopupType";
import {useDialogStore} from "~/store/dialog/dialogStore";
import type {
    LearningDirectoryNode,
    LearningFileNode,
    LearningFileSystemTree
} from "~~/package/types/LearningFileSystemTree";
import {LangType} from "~~/package/mapper/learning/model/LangType";
import {
    LearningDirectoryType, type UserLearningDirectoryTypes
} from "~/components/Learning/LearningMiddle/LearningMultiFileCode/LearningDirectory/config/LearningDirectoryType";
import {useLearningService} from "~/composables/service/useLearningService";
import {useAwsCredentialsService} from "~/composables/service/useAwsCredentialsService";

export type PathParams<T extends LearningDirectoryType> = T extends LearningDirectoryType.Default | LearningDirectoryType.ValidSet
    ? {
        directoryType: T;
        taskId: number;
    }
    : T extends LearningDirectoryType.StaticAnalysis
        ? {
            directoryType: T;
            taskId: number;
            userUuid: string;
            domainId: number | string;
        }
        : {
            directoryType: T;
            taskId: number;
            userUuid: string;
            domainId: number | string;
            isSubmission: boolean;
            isSaving: boolean;
        };

export const useLearningDirectoryService = () => {
    const config = { public: applicationContext.getConfig() }
    const learningStore = useLearningStore();
    const userStore = useUserStore();
    const awsCredentialsService = useAwsCredentialsService();
    const s3 = ref<S3Client>();
    const dialogStore = useDialogStore();
    const learningService = useLearningService();
    const webContainerService = useWebContainerService();

    const getBucket = (directoryType: LearningDirectoryType) => {
        switch (directoryType) {
            case LearningDirectoryType.StaticAnalysis:
                return config.public.LEARNING_USER_ANALYSIS_DIRECTORY_BUCKET;
            case LearningDirectoryType.UserLevelAssessment:
            case LearningDirectoryType.UserProSkillAssure:
            case LearningDirectoryType.UserContest:
            case LearningDirectoryType.UserLearning:
                return config.public.LEARNING_USER_DIRECTORY_BUCKET;
            default:
                return config.public.LEARNING_DEFAULT_DIRECTORY_BUCKET;
        }
    }

    const isUserDirectory = (directoryType: LearningDirectoryType): boolean => {
        return [LearningDirectoryType.UserLevelAssessment, LearningDirectoryType.UserProSkillAssure, LearningDirectoryType.UserContest, LearningDirectoryType.UserLearning].includes(directoryType);
    };

    const defaultDirectoryPath = (taskId: number): string => {
        return `${config.public.ENVIRONMENT}/${taskId.toString()}`
    }

    const validSetDirectoryPath = (taskId: number): string => {
        return `${config.public.ENVIRONMENT}/${taskId.toString()}/.validset`;
    }

    const analysisSetDirectoryPath = (domainId:number, taskId: number, userUuid: string): string => {
        return `reports/custom_assessment/${domainId.toString()}/${userUuid}/${taskId.toString()}`;
    }

    const domainPath = (type: LearningDirectoryType): string => {
        if (type === LearningDirectoryType.UserLevelAssessment) return 'level_assessment'
        else if (type === LearningDirectoryType.UserProSkillAssure) return 'custom_assessment'
        else if (type === LearningDirectoryType.UserContest) return 'contest'
        else if (type === LearningDirectoryType.UserLearning) return 'training'
        return "unknown"
    }

    const userDirectoryPath = (pathParams: PathParams<UserLearningDirectoryTypes>): string => {
        const runType = pathParams.isSaving ? "save"
            : pathParams.isSubmission ? "submit_answer" : "run_code";
        return `${config.public.ENVIRONMENT}/${domainPath(pathParams.directoryType)}/${pathParams.domainId}/${pathParams.userUuid}/${pathParams.taskId}/${runType}`
    }

    const prefixParam = (pathParams: PathParams<LearningDirectoryType>): string => {
        if (pathParams.directoryType === LearningDirectoryType.Default) return defaultDirectoryPath(pathParams.taskId);
        else if (pathParams.directoryType === LearningDirectoryType.ValidSet) return validSetDirectoryPath(pathParams.taskId);
        else if (pathParams.directoryType === LearningDirectoryType.StaticAnalysis) return analysisSetDirectoryPath(pathParams.domainId as number, pathParams.taskId, pathParams.userUuid);
        else return userDirectoryPath(pathParams);
    }

    const downloadDirectory = (pathParams: PathParams<LearningDirectoryType>): void => {
        learningStore.directory.isInitDirectory = false;
        if (typeof window !== 'undefined') {
            if (learningStore.directory.isInitCredentials) {
                handleCredentials(undefined, pathParams);
            } else {
                awsCredentialsService.createCredentials().then((ok) => {
                    if (!ok || !learningStore.directory.credentialProvider) {
                        console.error("Failed to initialize AWS credentials. Directory request aborted.");
                        return;
                    }
                    handleCredentials(undefined, pathParams);
                });
            }
        }
    };

    const handleCredentials = (err: Error | undefined, pathParams: PathParams<LearningDirectoryType>) => {
        if (err) throw err
        if (!learningStore.directory.credentialProvider) {
            console.error("Credential provider is missing. Directory request aborted.");
            return;
        }
        if (!s3.value) s3.value = new S3Client({
            region: 'ap-northeast-2',
            credentials: learningStore.directory.credentialProvider,
            useAccelerateEndpoint: true
        });

        requestDirectory(pathParams);
    }

    const requestDirectory = async (pathParams: PathParams<LearningDirectoryType>): Promise<void> => {
        console.time("requestDirectory")
        const params = {
            Bucket: getBucket(pathParams.directoryType),
            Prefix: prefixParam(pathParams)
        };

        try {
            const data = await s3.value!.send(new ListObjectsCommand(params));
            if(!data.Contents) data.Contents = [];

            if (data.Contents) {
                console.timeLog("requestDirectory", "end request list")
                const s3Objects = pathParams.directoryType !== LearningDirectoryType.ValidSet
                    ? data.Contents.filter(object => !/\/\./.test(object.Key as string) || isHidePath(object.Key as string))
                    : data.Contents.filter(object => !isHidePath(object.Key as string));

                const files = s3Objects.map(object => {
                    return {
                        Key: (object.Key as string).replace(params.Prefix, ""),
                        Size: object.Size,
                        Hide: isHidePath(object.Key as string)
                    }
                })

                if (pathParams.directoryType === LearningDirectoryType.ValidSet) {
                    makeDirectoryStructure(params, files, (tree: LearningFileSystemTree) => {
                        learningStore.directory.validSetTree = tree;
                    });
                } else if (pathParams.directoryType === LearningDirectoryType.StaticAnalysis) {
                    getAnalysisFileData(params, files);
                } else if (isUserDirectory(pathParams.directoryType) && files.length === 0 && pathParams.directoryType !== LearningDirectoryType.Default && !pathParams.isSubmission) {
                    requestDirectory({...pathParams, directoryType: LearningDirectoryType.Default});
                } else {
                    makeDirectoryStructure(params, files, initMakeDirectoryStructureHandler)
                }
            }
        } catch (err) {
            throw err;
        }
    }

    const initMakeDirectoryStructureHandler = (tree: LearningFileSystemTree) => {
        console.timeLog("requestDirectory", "end all file")
        console.timeEnd("requestDirectory")
        learningStore.directory.selectedFiles = [];
        learningStore.directory.tree = tree
        learningStore.directory.isInitDirectory = true;
        if (learningStore.isFrontendLearning) webContainerService.startWebContainer()
    }

    const isHidePath = (path: string) => {
        if(learningStore.task.langType === LangType.HTML_CSS_JAVASCRIPT) {
            return [/\.validate/, /\.assets/].some(regex => regex.test(path))
        } else {
            return [/hiddenFiles/, /.env/].some(regex => regex.test(path))
        }
    }

    const isLiveServer = () => {
        const packageJson = learningStore.directory.tree['package.json'] as LearningFileNode;
        let isLiveServerLearning = false
        if(!!packageJson && packageJson.file) {
            const contents = packageJson.file.contents.toString();
            isLiveServerLearning = /['"]dev['"]\s*:\s*['"]live-server['"]/.test(contents);
        }
        return isLiveServerLearning;
    }

    const makeDirectoryStructure = (
        params: { Bucket: string; Prefix: string } | undefined,
        files: Array<{Key: string, Size?: number, Hide?: boolean}>,
        handler: (tree: LearningFileSystemTree, isSuccess?: boolean) => void,
        tree = {} as LearningFileSystemTree
    ) => {
        const root = {...tree} as LearningFileSystemTree & { hide: boolean };
        const promiseList: Promise<void>[] = [];

        files.forEach((file, index) => {
            const parts = (file.Key as string).split('/').filter(part => part !== '');

            let currentDirectory: LearningFileSystemTree = root;

            parts.forEach((part, index) => {
                if (!currentDirectory[part]) {
                    currentDirectory[part] = {} as LearningDirectoryNode | LearningFileNode;
                }

                currentDirectory = currentDirectory[part];

                if (index < parts.length - 1) {
                    if (!currentDirectory.directory) {
                        currentDirectory.directory = {} as LearningFileSystemTree;
                        currentDirectory.hide = /^\./.test(part as string) || isHidePath(part);
                    }
                    currentDirectory = currentDirectory.directory;
                } else if ((file.Size as number > 0 || part.includes('.'))) {
                    currentDirectory.file = {
                        contents: ""
                    };
                    currentDirectory.hide = file.Hide
                    if (params) promiseList.push(getFileData(params, file.Key as string, currentDirectory.file))
                }
            });
        });


        if (promiseList.length > 0) {
            Promise.all(promiseList)
                .then(() => {
                    handler(root, true)
                    initLiveServerSettings();
                })
                .catch(() => {
                    handler(root, false);
                });
        } else handler(root, true);
    }
    
    const initLiveServerSettings = () => {
        if(isLiveServer() && !!learningStore.directory.tree.assets) {
            (learningStore.directory.tree.assets as LearningDirectoryNode).hide = true;
        }
    }

    const getAnalysisFileData = (
        params: { Bucket: string; Prefix: string },
        files: Array<{Key: string, Size?: number, Hide?: boolean}>,
    ) => {
        const promiseList: Promise<void>[] = [];
        const tree: LearningFileSystemTree = {};
        files.forEach((file, index) => {
            if(file.Key) {
                tree[file.Key] = {file: {contents: ""}} as LearningFileNode
                promiseList.push(getFileData(params, file.Key, tree[file.Key].file))
            }
        });

        if (promiseList.length > 0) {
            Promise.all(promiseList)
                .then(() => {
                    learningStore.directory.analysisSetTree = tree;
                })
                .catch(() => {
                    learningStore.directory.analysisSetTree = tree;
                });
        } else learningStore.directory.analysisSetTree = tree;
    }

    const getFileData = (params: { Bucket: string, Prefix: string }, path: string, file: {
        contents: string | Uint8Array
    }): Promise<void> => {
        return new Promise((resolve, reject) => {
            let cloudFrontDomain = config.public.LEARNING_DEFAULT_DIRECTORY_BUCKET_CLOUDFRONT_DOMAIN;
            let cacheTimeStamp = '';
            let requestInit: RequestInit = {}
            if(params.Bucket === config.public.LEARNING_USER_DIRECTORY_BUCKET) {
                cloudFrontDomain = config.public.USER_DIRECTORY_BUCKET_CLOUDFRONT_DOMAIN
                requestInit = {cache: 'no-cache'};
                cacheTimeStamp = `?ts=${Date.now()}`
            } else if(params.Bucket === config.public.LEARNING_USER_ANALYSIS_DIRECTORY_BUCKET) {
                cloudFrontDomain = config.public.LEARNING_USER_ANALYSIS_DIRECTORY_BUCKET_CLOUDFRONT_DOMAIN;
            }

            const cloudFrontUrl = `${cloudFrontDomain}/${params.Prefix}${path}${cacheTimeStamp}`;

            fetch(cloudFrontUrl, requestInit)
                .then(response => {
                    if (!response.ok) {
                        console.log(`Failed to fetch from CloudFront: ${response.statusText}`);
                        resolve(undefined);
                    }
                    return response.arrayBuffer();
                })
                .then(data => {
                    const isImage = /\.(jpg|jpeg|png|gif)$/i.test(path);
                    if(isImage) {
                        file.contents = new Uint8Array(data);
                    } else {
                        file.contents = new TextDecoder().decode(new Uint8Array(data));
                    }
                    resolve(undefined);
                })
                .catch(error => {
                    console.log(error);
                    resolve(undefined);
                });
        });
    }


    const syncData = (pathParams: PathParams<UserLearningDirectoryTypes>, isReload = false, handler?: () => void): void => {
        if (isReload || !learningStore.isFrontendLearning) syncDirectory();
        syncS3UserData(pathParams, isReload, handler);
    }

    const syncDirectory = () => {
        learningStore.directory.isInitDirectory = false;
        if (learningStore.directory.modifiedFiles.length > 0)
            learningStore.directory.modifiedFiles.map(file =>
                setFileToTree(file.path, file.file.file.contents)
            );
        learningStore.directory.isInitDirectory = true;
    }

    const setFileToTree = (path: string, contents?: string | Uint8Array) => {
        const parts = path.split('/').filter(part => part !== '');

        let currentDirectory = learningStore.directory.tree;

        parts.forEach(part => {
            if (!currentDirectory[part]) return;

            currentDirectory = currentDirectory[part];

            if (!currentDirectory.directory)
                currentDirectory.file = {
                    contents: contents || ""
                };

            currentDirectory = currentDirectory.directory;
        });
    }

    const syncS3UserData = async (pathParams: PathParams<UserLearningDirectoryTypes>, isReload = false, handler?: () => void): Promise<void> => {
        if (!learningStore.directory.isInitCredentials) return;
        if (!s3.value) s3.value = new S3Client({
            region: 'ap-northeast-2',
            credentials: learningStore.directory.credentialProvider,
            useAccelerateEndpoint: true
        });

        const defaultPath = userDirectoryPath(pathParams);
        // 첫 await 전에 읽는다 — 업로드 중 다음 문항 진입이 이 플래그를 끈다.
        const hasUnsubmittedEdit = learningStore.isChangedButNotSubmit;

        const params = {
            Bucket: config.public.LEARNING_USER_DIRECTORY_BUCKET,
            Prefix: defaultPath
        };

        try {
            const data = await s3.value.send(new ListObjectsCommand(params));
            if(!data.Contents) data.Contents = [];

            if (data.Contents) {
                const fileList = await getFileList(isReload, data.Contents.length === 0);
                if (fileList.length === 0) return;

                if (isReload) fileListUpload(fileList, defaultPath, pathParams.taskId, hasUnsubmittedEdit, pathParams.isSubmission, pathParams.isSaving, isReload, handler);
                else
                    removeDirectory(pathParams).then(() =>
                        fileListUpload(fileList, defaultPath, pathParams.taskId, hasUnsubmittedEdit, pathParams.isSubmission, pathParams.isSaving, isReload, handler)
                    )
            }
        } catch (err) {
            throw err;
        }
    };

    const getFileList = async (isReload: boolean, isFirstReload: boolean) => {
        let fileList: { path: string, contents?: string | Uint8Array }[] = [];
        if (isReload && !isFirstReload) {
            fileList = learningStore.directory.modifiedFiles.map(modifiedFile => {
                return {
                    path: getModifiedPath(modifiedFile.path),
                    contents: modifiedFile.file.file.contents
                };
            });
        } else fileList = flattenFileSystemTree(learningStore.directory.tree, "", true);

        return fileList;
    }

    const getModifiedPath = (path: string) => {
        return path.startsWith('/') ? path.substring(1) : path;
    }

    const fileListUpload = (
        fileList: {path: string, contents?: string | Uint8Array}[],
        defaultPath: string,
        taskId: number,
        hasUnsubmittedEdit: boolean,
        isSubmitAnswer: boolean,
        isSaving: boolean,
        isReload: boolean,
        handler?: () => void
    ) => {
        Promise.all(fileList.map((file, index) => fileUpload(file, index, defaultPath)))
            .then((isSuccessList: boolean[]) => {
                const isUploadSuccessful = isSuccessList.every(isSuccess => isSuccess);
                if (!isUploadSuccessful) {
                    if (!isReload && isSaving) learningStore.directory.isUploaded = false;
                    if (isSubmitAnswer) {
                        openConnectFailPopup()
                        throw new Error('Upload failed');
                    }
                }

                // isReload(수정 파일만 업로드)도 save 경로다 — 프론트엔드 멀티파일의 1초 자동 저장이 이 경로다.
                if (isUploadSuccessful && isSaving && !isSubmitAnswer && hasUnsubmittedEdit) learningStore.unsubmittedChangeByTaskId[taskId] = true;
                if (isUploadSuccessful && !isReload && isSaving && !isSubmitAnswer) learningStore.directory.isUploaded = true;
                if (handler) handler()
            })
    }

    const openConnectFailPopup = () => {
        dialogStore.open({
            type: DialogType.LearningConfirmPopup,
            props: {
                type: LearningConfirmPopupType.CONNECT_FAIL
            },
            confirm: {
                event: () => {
                }
            },
        })
    }

    const flattenFileSystemTree = (tree: FileSystemTree, path = '', skipNodeModules = false): {
        path: string,
        contents?: string | Uint8Array
    }[] => {
        const flattenedTree: { path: string, contents?: string | Uint8Array }[] = [];

        for (const [name, node] of Object.entries(tree)) {
            const newPath = path ? `${path}/${name}` : name;

            if (skipNodeModules && newPath.startsWith('node_modules') || newPath.startsWith('.validset')) {
                // skipNodeModules가 true이고 node_modules 디렉토리인 경우
                // .validset 디렉토리인 경우
                // 건너뜀
                continue;
            }
            if ('directory' in node) {
                // 디렉토리인 경우 하위 디렉토리를 재귀적으로 탐색
                flattenedTree.push(...flattenFileSystemTree(node.directory, newPath, skipNodeModules));
            } else if (node.file) {
                // 파일인 경우 파일 정보를 배열에 추가
                flattenedTree.push({path: newPath, contents: node.file.contents});
            }
        }

        return flattenedTree;
    }

    const fileUpload = async (file: {
        path: string,
        contents?: string | Uint8Array
    }, index: number, defaultPath: string): Promise<boolean> => {
        try {
            await s3.value!.send(new PutObjectCommand({
                Bucket: config.public.LEARNING_USER_DIRECTORY_BUCKET,
                Key: `${defaultPath}/${file.path}`,
                Body: file.contents
            }));
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    const removeDirectory = async (pathParams: PathParams<UserLearningDirectoryTypes>): Promise<void> => {
        const params = {
            Bucket: config.public.LEARNING_USER_DIRECTORY_BUCKET,
            Prefix: userDirectoryPath(pathParams),
        };

        try {
            const data = await s3.value!.send(new ListObjectsCommand(params));
            if(!data.Contents) data.Contents = [];

            if (data.Contents && data.Contents.length > 0) {
                await s3.value!.send(new DeleteObjectsCommand({
                    Bucket: config.public.LEARNING_USER_DIRECTORY_BUCKET,
                    Delete: {
                        Objects: data.Contents.map(object => {
                            return {Key: object.Key}
                        })
                    }
                }));
            }
        } catch (err) {
            console.log(err);
        }
    }

    const addRollbackPath = (path: string) => {
        const normalizedPath = getNormalizedPath(path);
        if (learningStore.directory.rollbackPathList.includes(normalizedPath)) {
            learningStore.directory.rollbackPathList = learningStore.directory.rollbackPathList.filter((rollbackPath) => rollbackPath !== normalizedPath)
        } else {
            learningStore.directory.rollbackPathList.push(normalizedPath)
        }
    }

    const addRollbackDirectory = (fileSystemTree: FileSystemTree, path: string) => {
        const pathList = getNormalizedPathList(fileSystemTree, path);
        const preCheckedPathList = learningStore.directory.rollbackPathList
            .filter((path) => pathList.includes(path));

        if (pathList.length === preCheckedPathList.length) {
            learningStore.directory.rollbackPathList = learningStore.directory.rollbackPathList.filter((path) => !pathList.includes(path));
        } else {
            learningStore.directory.rollbackPathList = [...new Set([...learningStore.directory.rollbackPathList, ...pathList])];
        }

        learningStore.directory.rollbackPathList = learningStore.directory.rollbackPathList.filter((path) => path.indexOf(".validate"))
    }

    const getNormalizedPath = (path: string) => {
        return path.charAt(0) === "/" ? path.slice(1) : path;
    }

    const getNormalizedPathList = (fileSystemTree: FileSystemTree, path: string) => {
        return flattenFileSystemTree(fileSystemTree, path, true)
            .map(path => getNormalizedPath(path.path));
    }

    const rollbackFiles = (pathParams: PathParams<UserLearningDirectoryTypes>, handler: (isSuccess: boolean) => void) => {
        learningStore.directory.isInitDirectory = false;
        if (!s3.value) s3.value = new S3Client({
            region: 'ap-northeast-2',
            credentials: learningStore.directory.credentialProvider,
            useAccelerateEndpoint: true
        });

        const params = {
            Bucket: config.public.LEARNING_DEFAULT_DIRECTORY_BUCKET,
            Prefix: defaultDirectoryPath(learningStore.task.id),
        };
        const files = learningStore.directory.rollbackPathList.map(path => {
            return {Key: `/${path}`, Size: 0}
        })

        makeDirectoryStructure(params, files, (tree: LearningFileSystemTree, isSuccess) => {
            if (isSuccess) {
                learningStore.directory.tree = tree;
                updateSelectedFiles(files);
                learningStore.directory.isInitDirectory = true;
                syncS3UserData(pathParams, false);
                learningStore.directory.rollbackPathList = [];
            }
            handler(isSuccess as boolean);
            if (learningStore.isFrontendLearning) webContainerService.runWebContainer(true);
        }, learningStore.directory.tree);
    }

    const getModifiedFiles = () => {
        const files = learningStore.directory.modifiedFiles.map(
            modifiedFile => {
                return {Key: modifiedFile.path, Size: 0, Hide: false}
            }
        );

        makeDirectoryStructure(undefined, files, (tree: LearningFileSystemTree) => {
            learningStore.directory.rollbackTree = tree
        })
    }

    const updateSelectedFiles = (files: {Key: string, Size: number }[]) => {
        files.map(file => {
            learningStore.updateSelectedDirectoryFile(getFileFromTree(file.Key));
        });
    }

    const getFileFromTree = (path: string): {path: string, contents: string | Uint8Array} | null => {
        let contents: string | Uint8Array = "";
        const parts = path.split('/').filter(part => part !== '');

        let currentDirectory = learningStore.directory.tree;

        parts.forEach(part => {
            if (!currentDirectory[part]) return;

            currentDirectory = currentDirectory[part];

            if (!currentDirectory.directory) {
                contents = currentDirectory.file.contents;
            }

            currentDirectory = currentDirectory.directory;
        });

        return contents !== "" ? {path, contents} : null;
    }

    const saveMultipleCode = (isReload = false, isSaving = false, isSubmission = false, handler?: () => void) => {
        syncData({
            directoryType: learningStore.getUserLearningDirectoryType,
            domainId: learningStore.getDomainId,
            taskId: learningStore.task.id,
            userUuid: userStore.userV2.uuid,
            isSubmission,
            isSaving
        }, isReload, handler);
    }

    return {
        // Test 코드에서 사용하기 위하여 노출
        prefixParam,
        downloadDirectory,
        syncData,
        addRollbackPath,
        addRollbackDirectory,
        getNormalizedPath,
        getNormalizedPathList,
        rollbackFiles,
        getModifiedFiles,
        saveMultipleCode
    };
}
